import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { ModalContext, __setConfirmImpl } from "./confirm";

export function ConfirmProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const current = queue[0] || null;

  const enqueueConfirm = useCallback(
    (options) =>
      new Promise((resolve) => {
        setQueue((q) => [...q, { id: Date.now() + Math.random(), options, resolve }]);
      }),
    []
  );

  useEffect(() => {
    __setConfirmImpl(enqueueConfirm);
    return () =>
      __setConfirmImpl(async () => {
        throw new Error("ConfirmProvider is not mounted");
      });
  }, [enqueueConfirm]);

  const onClose = (result) => {
    if (!current) return;
    current.resolve(result);
    setQueue(([, ...rest]) => rest);
  };

  const ctxValue = useMemo(() => ({ enqueueConfirm }), [enqueueConfirm]);
  const options = current?.options ?? {};
  const {
    title = "標題",
    content = "內容",
    showCancel = false,
    cancelText = "取消",
    showConfirm = false,
    confirmText = "確認",
    confirmColor = "red",
    maskClosable = false,
    keyboardClosable = false,
  } = options;

  return (
    <ModalContext.Provider value={ctxValue}>
      {children}
      <Dialog
        open={!!current}
        role="alertdialog"
        maxWidth="xs"
        fullWidth
        onClose={(_e, reason) => {
          if (reason === "backdropClick" && !maskClosable) return;
          if (reason === "escapeKeyDown" && !keyboardClosable) return;
          onClose(false);
        }}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">{content}</DialogContentText>
        </DialogContent>
        {(showCancel || showConfirm) && (
          <DialogActions sx={{ px: 3, pb: 3 }}>
            {showCancel && (
              <Button variant="text" onClick={() => onClose(false)}>
                {cancelText}
              </Button>
            )}
            {showConfirm && (
              <Button
                variant="contained"
                color={confirmColor === "red" ? "error" : "primary"}
                onClick={() => onClose(true)}
              >
                {confirmText}
              </Button>
            )}
          </DialogActions>
        )}
      </Dialog>
    </ModalContext.Provider>
  );
}
